import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('click', this.#handleBundleRemove);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('click', this.#handleBundleRemove);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const removedVariantId = cartItemRowToRemove.dataset.variantId;
    const parentVariantId = cartItemRowToRemove.dataset.parentVariantId;

    // Removing a bag → also remove linked embroidery items
    const embroideryRows = removedVariantId
      ? this.refs.cartItemRows.filter((row) => row.dataset.parentVariantId === removedVariantId)
      : [];

    // Removing an embroidery item → keep the parent bag, but strip the orphan
    // embroidery properties off it (Thread-Color, line_1, line_2, SKU, etc).
    // Otherwise 3PL reads the stale instructions on the bag and ships a blank pocket.
    const parentBagRow = parentVariantId
      ? this.refs.cartItemRows.find((row) => row.dataset.variantId === parentVariantId && !row.dataset.parentVariantId)
      : null;

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
      ...embroideryRows,
    ];

    // Only strip the bag's properties if no other embroidery line items remain linked to it
    const otherEmbroideryForBag = parentBagRow
      ? this.refs.cartItemRows.filter(
          (row) =>
            row.dataset.parentVariantId === parentBagRow.dataset.variantId && !rowsToRemove.includes(row)
        )
      : [];
    const shouldStripBagProperties =
      parentBagRow && parentBagRow.dataset.key && otherEmbroideryForBag.length === 0;

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });

    if (shouldStripBagProperties) {
      this.#removeEmbroideryAndCleanBag(rowsToRemove, parentBagRow.dataset.key);
    } else if (embroideryRows.length > 0) {
      // Batch remove all linked items via /cart/update.js
      const updates = {};
      rowsToRemove.forEach((row) => {
        if (row.dataset.key) updates[row.dataset.key] = 0;
      });
      this.#batchRemoveItems(updates);
    } else {
      this.updateQuantity({
        line,
        quantity: 0,
        action: 'clear',
      });
    }
  }

  /**
   * Removes the embroidery component from a bundled cart line and strips the parent bag's
   * underscore-prefixed customization properties so it reverts to a plain bag in the cart
   * AND in the eventual order data Fulfil sees (no orphan embroidery instructions).
   *
   * Wired to the × button rendered in `snippets/cart-products.liquid` for non-parent bundle
   * components. Event delegation: listens on the cart-items-component itself.
   */
  #handleBundleRemove = async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const btn = target?.closest('[data-action="remove-embroidery"]');
    if (!btn) return;

    event.preventDefault();

    const componentKey = btn.getAttribute('data-component-key');
    const parentKey = btn.getAttribute('data-parent-key');
    if (!componentKey || !parentKey) return;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('clear:user-action');
    this.#disableCartItems();
    const { cartTotal } = this.refs;
    cartTotal?.shimmer();

    // Optimistic UI: hide the component row immediately so the click feels instant
    const componentRow = btn.closest('.cart-items__bundle-component');
    componentRow?.setAttribute('hidden', '');

    try {
      // 1. Remove the embroidery cart line
      await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', {
        body: JSON.stringify({ id: componentKey, quantity: 0 }),
      }));

      // 2. Clear the bag's customization properties (all underscore-prefixed props were added
      // by the embroidery flow; setting properties to {} drops them so Fulfil sees a plain bag).
      // Request the section back so we can morph in one shot.
      const finalRes = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', {
        body: JSON.stringify({
          id: parentKey,
          properties: {},
          sections: this.sectionId,
          sections_url: window.location.pathname,
        }),
      }));
      const finalText = await finalRes.text();
      const finalData = JSON.parse(finalText);

      if (finalData.errors) {
        console.error('Bundle remove error:', finalData.errors);
        componentRow?.removeAttribute('hidden');
        return;
      }

      resetShimmer(this);
      this.#updateQuantitySelectors(finalData);

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: finalData.item_count,
          source: 'cart-items-component',
          sections: finalData.sections,
        })
      );

      if (finalData.sections && finalData.sections[this.sectionId]) {
        morphSection(this.sectionId, finalData.sections[this.sectionId]);
      }

      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      console.error('Bundle remove failed:', error);
      componentRow?.removeAttribute('hidden');
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  };

  /**
   * Removes multiple cart items at once by their keys.
   * @param {Record<string, 0>} updates - Map of cart item keys to quantity 0.
   */
  #batchRemoveItems(updates) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('clear:user-action');

    this.#disableCartItems();

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          console.error('Batch remove error:', parsedResponseText.errors);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Strips embroidery line-item properties off the parent bag and removes the embroidery line(s).
   * Why: when an embroidery upsell is removed alone, the bag was left with stale Thread-Color /
   * line_1 / SKU properties — 3PL reads those and ships a blank pocket since no embroidery line
   * is in the order. Cleaning the bag's properties prevents the orphan-instructions case.
   *
   * @param {HTMLElement[]} rowsToRemove - Embroidery rows being removed (and their nested lines).
   * @param {string} parentKey - Cart line key of the parent bag whose properties to clean.
   */
  #removeEmbroideryAndCleanBag(rowsToRemove, parentKey) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('clear:user-action');
    const EMBROIDERY_PROPERTY_KEYS = new Set([
      'Thread-Color',
      'Color',
      'line_1',
      'line_2',
      'top_line',
      'bottom_line',
      'upsell_price',
      'Upsell Price',
      'SKU',
      'sku',
      '_parent_id',
    ]);

    this.#disableCartItems();
    const { cartTotal } = this.refs;
    cartTotal?.shimmer();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    const sectionsParam = Array.from(sectionsToUpdate).join(',');

    const embroideryUpdates = {};
    rowsToRemove.forEach((row) => {
      if (row.dataset.key && row.dataset.key !== parentKey) {
        embroideryUpdates[row.dataset.key] = 0;
      }
    });

    // Shopify line-item properties are IMMUTABLE — /cart/change.js with new properties
    // returns 200 but does not actually mutate them. To clean the bag we have to
    // remove the bag and re-add it with cleaned properties.
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        const parentItem = cart.items?.find((item) => item.key === parentKey);
        if (!parentItem) {
          // Bag not in cart anymore — just remove the embroidery line(s) with sections
          return fetch(
            `${Theme.routes.cart_update_url}`,
            fetchConfig('json', {
              body: JSON.stringify({
                updates: embroideryUpdates,
                sections: sectionsParam,
                sections_url: window.location.pathname,
              }),
            })
          );
        }

        const cleanedProperties = {};
        if (parentItem.properties) {
          for (const [key, value] of Object.entries(parentItem.properties)) {
            if (!EMBROIDERY_PROPERTY_KEYS.has(key)) cleanedProperties[key] = value;
          }
        }

        const sellingPlanId = parentItem.selling_plan_allocation?.selling_plan?.id;
        const variantId = parentItem.variant_id;
        const bagQuantity = parentItem.quantity;

        // 1) Remove embroidery line(s) + the polluted bag in one /cart/update.js call
        const removeUpdates = { ...embroideryUpdates, [parentKey]: 0 };

        return fetch(
          `${Theme.routes.cart_update_url}`,
          fetchConfig('json', {
            body: JSON.stringify({ updates: removeUpdates }),
          })
        ).then(() => {
          // 2) Re-add the bag with cleaned properties; ask for sections to render
          const addItem = {
            id: variantId,
            quantity: bagQuantity,
            properties: cleanedProperties,
          };
          if (sellingPlanId) addItem.selling_plan = sellingPlanId;

          return fetch(
            `${Theme.routes.cart_add_url}`,
            fetchConfig('json', {
              body: JSON.stringify({
                items: [addItem],
                sections: sectionsParam,
                sections_url: window.location.pathname,
              }),
            })
          );
        });
      })
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          console.error('Embroidery cleanup error:', parsedResponseText.errors);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
